export type BusinessFlowActorView = {
  id: string;
  roleCode?: string;
  human?: boolean;
  title: string;
  youDo: string;
  youNever?: string;
  talksTo?: string[];
};

export type BusinessFlowStageView = {
  id: string;
  label: string;
  owner: string;
  entryEvidence?: string;
};

export type BusinessFlowView = {
  title: string;
  summary: string;
  actors: BusinessFlowActorView[];
  edges: Array<{ from: string; to: string; via?: string; purpose?: string }>;
  stages: BusinessFlowStageView[];
};

function asString(value: unknown): string {
  return String(value || '').trim();
}

export function parseBusinessFlowFromStreamYaml(yamlRoot: unknown): BusinessFlowView | null {
  const root = yamlRoot && typeof yamlRoot === 'object'
    ? ((yamlRoot as Record<string, unknown>).stream || yamlRoot) as Record<string, unknown>
    : null;
  if (!root) return null;
  const raw = root.businessFlow || root.business_flow;
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const title = asString(source.title);
  const actors = Array.isArray(source.actors) ? source.actors : [];
  const parsedActors = actors
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const row = entry as Record<string, unknown>;
      const roleCode = asString(row.roleCode) || undefined;
      const id = asString(row.id) || roleCode || (row.human === true ? 'human' : '');
      const actorTitle = asString(row.title);
      if (!id || !actorTitle) return null;
      return {
        id,
        roleCode,
        human: row.human === true,
        title: actorTitle,
        youDo: asString(row.youDo),
        youNever: asString(row.youNever) || undefined,
        talksTo: Array.isArray(row.talksTo) ? row.talksTo.map((item) => asString(item)).filter(Boolean) : [],
      } satisfies BusinessFlowActorView;
    })
    .filter((actor): actor is BusinessFlowActorView => Boolean(actor));
  if (!title || parsedActors.length === 0) return null;
  const stages = Array.isArray(source.stages) ? source.stages : [];
  const edges = Array.isArray(source.edges) ? source.edges : [];
  return {
    title,
    summary: asString(source.summary),
    actors: parsedActors,
    edges: edges
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const row = entry as Record<string, unknown>;
        const from = asString(row.from);
        const to = asString(row.to);
        if (!from || !to) return null;
        return { from, to, via: asString(row.via) || undefined, purpose: asString(row.purpose) || undefined };
      })
      .filter((edge): edge is BusinessFlowView['edges'][number] => Boolean(edge)),
    stages: stages
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const row = entry as Record<string, unknown>;
        const id = asString(row.id);
        const label = asString(row.label);
        const owner = asString(row.owner);
        if (!id || !label || !owner) return null;
        return { id, label, owner, entryEvidence: asString(row.entryEvidence) || undefined };
      })
      .filter((stage): stage is BusinessFlowStageView => Boolean(stage)),
  };
}

function mermaidId(value: string): string {
  return asString(value).replace(/[^A-Za-z0-9_]/g, '_') || 'Node';
}

export function buildBusinessFlowMermaid(flow: BusinessFlowView): string {
  const lines = ['flowchart TD'];
  for (const actor of flow.actors) {
    lines.push(`  ${mermaidId(actor.id)}["${actor.title.replace(/"/g, '')}"]`);
  }
  for (const edge of flow.edges) {
    const label = [edge.via, edge.purpose].filter(Boolean).join(': ').replace(/"/g, '');
    if (label) lines.push(`  ${mermaidId(edge.from)} -->|"${label}"| ${mermaidId(edge.to)}`);
    else lines.push(`  ${mermaidId(edge.from)} --> ${mermaidId(edge.to)}`);
  }
  return lines.join('\n');
}
